import { auth } from '../config/firebase';

export class AuthError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getAuthHeader = async (retryCount: number = 0): Promise<Record<string, string>> => {
  const maxRetries = 2;

  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new AuthError('No authenticated user found', 'NO_USER');
    }

    const token = await currentUser.getIdToken();
    if (!token) {
      throw new AuthError('Failed to retrieve authentication token', 'NO_TOKEN');
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }

    // Handle Firebase auth errors with retry logic for transient failures
    const authError = error as { code?: string; message?: string };

    // Retry on certain transient errors
    const isRetriableError =
      authError.code === 'auth/network-request-failed' ||
      authError.code === 'auth/too-many-requests';

    if (isRetriableError && retryCount < maxRetries) {
      console.warn(`Auth error (attempt ${retryCount + 1}/${maxRetries + 1}):`, authError.message);
      await sleep(Math.pow(2, retryCount) * 1000); // Exponential backoff
      return getAuthHeader(retryCount + 1);
    }

    if (authError.code) {
      throw new AuthError(`Authentication failed: ${authError.message}`, authError.code);
    }

    throw new AuthError('Unknown authentication error occurred', 'UNKNOWN');
  }
};
